import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from '@tanstack/react-router';
import { useEffect, useLayoutEffect, useState, type ReactNode } from 'react';
import { Controller, useForm, useWatch, type DeepPartialSkipArrayKey, type UseFormReturn } from 'react-hook-form';
import {
  DATE_FORMAT_LABELS,
  DATE_FORMATS,
  ELIMINATE_MODE_LABELS,
  ELIMINATE_MODES,
  FILE_LIMIT_MAX_MB,
  HOLD_DAYS_MAX,
  HOLD_DAYS_MIN,
  NUMBER_FORMAT_LABELS,
  NUMBER_FORMATS,
  organisationSettingsSchema,
  RETENTION_PATH,
  SUPPORTED_CURRENCIES,
  SUPPORTED_TIMEZONES,
  WHO_SELECTS,
  WHO_SELECTS_LABELS,
  isCurrencyCode,
  type AppSettingsView,
  type ModuleKey,
  type OrganisationSettings,
  type OrganisationSettingsInput,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Radio, RadioGroup } from '../../../components/Checkbox';
import { Field } from '../../../components/Field';
import { Input } from '../../../components/Input';
import { ModuleGate } from '../../../components/ModuleGate';
import { SaveBar } from '../../../components/SaveBar';
import { MultiSelect, Select } from '../../../components/Select';
import { SkeletonLines } from '../../../components/Skeleton';
import { Switch } from '../../../components/Switch';
import { ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { errorMessage } from '../../../lib/api-client';
import { useAppSettings } from '../../../lib/app-settings';
import { currencyLabel } from '../../../lib/currency-label';
import { useModules } from '../../../lib/modules';
import { useUnsavedWarning } from '../../../lib/use-unsaved-warning';
import { useCanEdit, useCanView } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { useSaveSettings } from '../api/settings-api';
import { VentureLogosCard } from '../components/VentureLogosCard';
import styles from './SystemSettingsPage.module.css';

const MONTHS = Array.from({ length: 12 }, (_, index) => ({
  value: String(index + 1),
  label: new Intl.DateTimeFormat('en-GB', { month: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(2026, index, 1))),
}));

const CURRENCY_OPTIONS = SUPPORTED_CURRENCIES.map((code) => ({ value: code, label: currencyLabel(code) }));

type SectionId = 'money' | 'dates' | 'uploads' | 'final' | 'tests' | 'data' | 'logos';

/** The index, in the drawing's order. Tests belongs to Online assessments, so it leaves the index with it (06 §13). */
const SECTIONS: readonly { readonly id: SectionId; readonly label: string; readonly module?: ModuleKey }[] = [
  { id: 'money', label: 'Money' },
  { id: 'dates', label: 'Dates and numbers' },
  { id: 'uploads', label: 'Uploads' },
  { id: 'final', label: 'Final selection' },
  { id: 'tests', label: 'Tests', module: 'assessments' },
  { id: 'data', label: 'Candidate data' },
  { id: 'logos', label: 'Venture logos' },
];

const sectionDomId = (id: SectionId): string => `system-settings-${id}`;

/** The form's values as `useWatch` gives them: a part may still be missing while its field mounts. */
type WatchedSettings = DeepPartialSkipArrayKey<OrganisationSettingsInput>;

/** Each setting with the name the save bar calls it by, and the card it sits in. */
const FIELDS: readonly { readonly label: string; readonly section: SectionId; readonly read: (settings: WatchedSettings) => unknown }[] = [
  { label: 'Currencies in use', section: 'money', read: (settings) => [...(settings.currencies ?? [])].sort() },
  { label: 'Reporting currency', section: 'money', read: (settings) => settings.reportingCurrency },
  { label: 'Default time zone', section: 'dates', read: (settings) => settings.defaultTimezone },
  { label: 'Fiscal year starts in', section: 'dates', read: (settings) => settings.fiscalYearStartMonth },
  { label: 'Date format', section: 'dates', read: (settings) => settings.dateFormat },
  { label: 'Large numbers', section: 'dates', read: (settings) => settings.numberFormat },
  { label: 'Largest CV', section: 'uploads', read: (settings) => settings.cvMaxMb },
  { label: 'Largest other document', section: 'uploads', read: (settings) => settings.documentMaxMb },
  { label: 'Who records the final selection', section: 'final', read: (settings) => settings.finalSelection?.whoSelects },
  { label: 'What “Eliminate” does', section: 'final', read: (settings) => settings.finalSelection?.eliminate },
  { label: 'Keep someone on hold for', section: 'final', read: (settings) => settings.finalSelection?.holdDays },
  { label: 'Send the test straight from Shortlist', section: 'tests', read: (settings) => settings.sendTestFromShortlist },
];

function formValuesOf(settings: OrganisationSettings): OrganisationSettingsInput {
  return { ...settings, currencies: [...settings.currencies], finalSelection: { ...settings.finalSelection } };
}

/** The settings whose value differs, by name. */
function changedFields(values: WatchedSettings, saved: WatchedSettings): readonly (typeof FIELDS)[number][] {
  return FIELDS.filter((field) => JSON.stringify(field.read(values)) !== JSON.stringify(field.read(saved)));
}

/** The version the form stands on. `movedFrom` is set when the base moved by itself, so the form is rebased from it. */
interface FormBase {
  readonly version: number;
  readonly settings: OrganisationSettingsInput;
  readonly movedFrom?: OrganisationSettingsInput;
}

/** A value the Admin changed from `was` stays theirs; one they left alone takes `now`. */
function kept<T>(mine: T, was: T, now: T): T {
  return JSON.stringify(mine) === JSON.stringify(was) ? now : mine;
}

/**
 * Review W3: this Admin's edits moved onto a newer version. Only what they changed from the version they started on is
 * kept; every other setting reads as the newer version has it, so "mine over theirs" overwrites nothing they didn't touch.
 * Every setting is named here, so a new one can't be added to the form without deciding how it rebases.
 */
export function rebasedSettings(mine: OrganisationSettingsInput, was: OrganisationSettingsInput, now: OrganisationSettingsInput): OrganisationSettingsInput {
  return {
    currencies: kept(mine.currencies, was.currencies, now.currencies),
    reportingCurrency: kept(mine.reportingCurrency, was.reportingCurrency, now.reportingCurrency),
    defaultTimezone: kept(mine.defaultTimezone, was.defaultTimezone, now.defaultTimezone),
    dateFormat: kept(mine.dateFormat, was.dateFormat, now.dateFormat),
    numberFormat: kept(mine.numberFormat, was.numberFormat, now.numberFormat),
    fiscalYearStartMonth: kept(mine.fiscalYearStartMonth, was.fiscalYearStartMonth, now.fiscalYearStartMonth),
    cvMaxMb: kept(mine.cvMaxMb, was.cvMaxMb, now.cvMaxMb),
    documentMaxMb: kept(mine.documentMaxMb, was.documentMaxMb, now.documentMaxMb),
    finalSelection: {
      whoSelects: kept(mine.finalSelection.whoSelects, was.finalSelection.whoSelects, now.finalSelection.whoSelects),
      eliminate: kept(mine.finalSelection.eliminate, was.finalSelection.eliminate, now.finalSelection.eliminate),
      holdDays: kept(mine.finalSelection.holdDays, was.finalSelection.holdDays, now.finalSelection.holdDays),
    },
    sendTestFromShortlist: kept(mine.sendTestFromShortlist, was.sendTestFromShortlist, now.sendTestFromShortlist),
  };
}

/**
 * ADMIN-15 / F19.12 (Admin redesign 2026-11-02, Option A of `5-settings/system-settings.html`): the organisation's
 * settings in two columns, an index that marks the section in view, and one save bar naming what changed, with Discard.
 * Saved together against the version they were read at, read at run time, no redeploy.
 */
export function SystemSettingsPage(): ReactNode {
  const canView = useCanView('admin_config');
  const settings = useAppSettings();
  if (!canView) return <ForbiddenState resource="the system settings" contact="Your admin" />;
  if (settings.isPending) return <SkeletonLines count={12} />;
  if (settings.error) return <ServerErrorState onRetry={() => void settings.refetch()} />;
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>System settings</h1>
        <p className={styles.lede}>How the whole organisation counts, dates and decides. Changes apply to everyone as soon as they are saved.</p>
      </header>
      {/* Never keyed on the version: a save by someone else must not throw away what this Admin has typed. */}
      <SettingsForm view={settings.data} />
    </div>
  );
}

function SettingsForm({ view }: { readonly view: AppSettingsView }): ReactNode {
  const canEdit = useCanEdit('admin_config');
  const save = useSaveSettings();
  const show = useToasts((state) => state.show);
  // What the form stands on: the version a save is checked against, and what "changed" is measured from. The form is
  // moved onto a new base only here, never by RHF on its own, so what the Admin typed can't be lost.
  const [base, setBase] = useState<FormBase>(() => ({ version: view.version, settings: formValuesOf(view.settings) }));
  // What was last sent, so the answer to our own save is never taken for someone else's.
  const [submitted, setSubmitted] = useState<OrganisationSettingsInput | null>(null);
  const form = useForm<OrganisationSettingsInput>({ resolver: zodResolver(organisationSettingsSchema), mode: 'onBlur', defaultValues: base.settings });
  const values = useWatch({ control: form.control });
  const changed = changedFields(values, base.settings);
  const isDirty = changed.length > 0;
  useUnsavedWarning(isDirty);
  const incoming = formValuesOf(view.settings);

  // A newer version arrived: someone saved, here or elsewhere. Ours, an untouched form, or one that already reads the
  // same: stand on it at once (anything typed since our save is kept). An edited form: keep the edits and the list of
  // them, and let the Admin choose (`SaveStatus`); the server refuses a save from the old version, so nothing of theirs
  // is overwritten unseen. Decided during render; the form follows before the screen is painted (below).
  if (view.version !== base.version) {
    const isOurs = submitted !== null && changedFields(incoming, submitted).length === 0;
    if (isOurs || !isDirty || changedFields(values, incoming).length === 0) {
      setBase({ version: view.version, settings: incoming, movedFrom: base.settings });
      if (isOurs) setSubmitted(null);
    }
  }
  const isOutdated = view.version !== base.version;
  // Review W3: what the other save changed, so the bar can say what "theirs" is.
  const theirs = isOutdated ? changedFields(incoming, base.settings) : [];

  // The form is a store of its own: when the base moved by itself, rebase the form's values onto it, keeping exactly
  // what this Admin had changed from the old one.
  useLayoutEffect(() => {
    if (base.movedFrom === undefined) return;
    form.reset(rebasedSettings(form.getValues(), base.movedFrom, base.settings));
  }, [base, form]);

  const send = async (input: OrganisationSettingsInput, version: number): Promise<void> => {
    setSubmitted(input);
    try {
      await save.mutateAsync({ settings: input, version });
      show({ tone: 'success', message: 'Settings saved', detail: 'Everyone sees the new settings from now on.' });
    } catch (cause) {
      setSubmitted(null);
      show({ tone: 'danger', message: "Couldn't save the settings", detail: errorMessage(cause) });
    }
  };

  const submit = form.handleSubmit((input) => send(input, base.version));

  /** "Save mine over theirs": their version is the new base, this Admin's changes go on top, and the save is checked against theirs. */
  const saveMineOverTheirs = (): void => {
    const version = view.version;
    // RHF's reset writes the values at once, so the submit below validates and sends the rebased form.
    form.reset(rebasedSettings(form.getValues(), base.settings, incoming));
    setBase({ version, settings: incoming });
    void form.handleSubmit((input) => send(input, version))();
  };

  /** "Reload theirs" drops this Admin's changes, so it says which, with Undo putting them back on top of theirs. */
  const reloadTheirs = (): void => {
    const mine = form.getValues();
    const was = base.settings;
    // Only what this Admin changed and theirs doesn't already read the same; a setting only they changed isn't "yours".
    const differs = changedFields(mine, incoming);
    const dropped = changedFields(mine, was)
      .filter((field) => differs.includes(field))
      .map((field) => field.label);
    const version = view.version;
    const settings = incoming;
    form.reset(settings);
    setBase({ version, settings });
    if (dropped.length === 0) return;
    show({
      tone: 'info',
      message: 'Showing their settings',
      detail: `Your ${dropped.length === 1 ? 'change' : 'changes'} to ${dropped.join(', ')} ${dropped.length === 1 ? 'was' : 'were'} dropped.`,
      action: { label: 'Undo', run: () => form.reset(rebasedSettings(mine, was, settings)) },
    });
  };

  const changedSections = new Set(changed.map((field) => field.section));
  return (
    <form className={styles.form} noValidate onSubmit={(event) => void submit(event)}>
      <div className={styles.layout}>
        <SectionIndex />
        <div className={styles.columns}>
          <div className={styles.column}>
            <Section id="money" isChanged={changedSections.has('money')}>
              <MoneyCard form={form} canEdit={canEdit} />
            </Section>
            <Section id="dates" isChanged={changedSections.has('dates')}>
              <DatesCard form={form} canEdit={canEdit} />
            </Section>
            <Section id="uploads" isChanged={changedSections.has('uploads')}>
              <UploadsCard form={form} canEdit={canEdit} />
            </Section>
            <Section id="data" isChanged={false}>
              <Card title="Keeping candidate data">
                <p className={styles.muted}>
                  Personal details are removed <strong className={styles.strong}>{retentionWords(view.retentionDays)}</strong> after a person’s last process closes. The
                  period and the dry run are set under{' '}
                  <Link className={styles.link} to={RETENTION_PATH}>
                    Retention
                  </Link>
                  .
                </p>
              </Card>
            </Section>
          </div>
          <div className={styles.column}>
            <Section id="final" isChanged={changedSections.has('final')}>
              <FinalSelectionCard form={form} canEdit={canEdit} />
            </Section>
            {/* ADR 0047: a shortcut past the manager's Yes/No and Contact, so it is off until HR chooses it. */}
            <ModuleGate module="assessments">
              <Section id="tests" isChanged={changedSections.has('tests')}>
                <TestsCard form={form} canEdit={canEdit} />
              </Section>
            </ModuleGate>
            <Section id="logos" isChanged={false}>
              <VentureLogosCard />
            </Section>
          </div>
        </div>
      </div>
      {canEdit && (
        <SaveBar status={<SaveStatus changed={changed.map((field) => field.label)} theirs={isOutdated ? theirs.map((field) => field.label) : null} />}>
          {isOutdated ? (
            // Review W3: a save from the old version would only be refused, so the choice replaces Discard and Save.
            <>
              <Button variant="quiet" disabled={save.isPending} onClick={reloadTheirs}>
                Reload theirs
              </Button>
              <Button variant="solid" isPending={save.isPending} onClick={saveMineOverTheirs}>
                Save mine over theirs
              </Button>
            </>
          ) : (
            <>
              <Button variant="quiet" disabled={!isDirty || save.isPending} onClick={() => form.reset(base.settings)}>
                Discard
              </Button>
              <Button variant="solid" type="submit" isPending={save.isPending} disabled={!isDirty}>
                Save settings
              </Button>
            </>
          )}
        </SaveBar>
      )}
    </form>
  );
}

/** What is unsaved; while someone else's save is newer, also what they changed and that this Admin's edits are kept. */
function SaveStatus({ changed, theirs }: { readonly changed: readonly string[]; readonly theirs: readonly string[] | null }): ReactNode {
  const unsaved =
    changed.length === 0 ? null : (
      <>
        <strong className={styles.strong}>
          {changed.length} unsaved {changed.length === 1 ? 'change' : 'changes'}
        </strong>
        <span>{changed.join(', ')}</span>
      </>
    );
  if (theirs !== null) {
    return (
      <span className={styles.status} role="status">
        <span className={styles.outdated}>
          Someone else saved settings{theirs.length > 0 ? ` (${theirs.join(', ')})` : ''}. Your changes are kept until you choose.
        </span>
        {unsaved}
      </span>
    );
  }
  if (unsaved === null) return <span role="status">Everything is saved</span>;
  return (
    <span className={styles.status} role="status">
      {unsaved}
    </span>
  );
}

/** The real period, as the Retention page writes it: 45 days is 45 days, never "2 months". */
function retentionWords(days: number): string {
  return `${days} ${days === 1 ? 'day' : 'days'}`;
}

function Section({ id, isChanged, children }: { readonly id: SectionId; readonly isChanged: boolean; readonly children: ReactNode }): ReactNode {
  return (
    <div id={sectionDomId(id)} className={styles.section} data-section={id} data-changed={isChanged}>
      {children}
    </div>
  );
}

/** The index beside the cards: it marks the section in view, and a click scrolls to it. */
function SectionIndex(): ReactNode {
  const modules = useModules();
  const shown = SECTIONS.filter((section) => section.module === undefined || modules[section.module]);
  const [active, setActive] = useState<SectionId>('money');
  const shownKey = shown.map((section) => section.id).join(',');

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const showing = shownKey.split(',');
    const ids = SECTIONS.map((section) => section.id).filter((id) => showing.includes(id));
    const inView = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = entry.target.getAttribute('data-section');
          if (id === null) continue;
          if (entry.isIntersecting) inView.add(id);
          else inView.delete(id);
        }
        const first = ids.find((id) => inView.has(id));
        if (first !== undefined) setActive(first);
      },
      // The band a third of the way down the window decides which section you are reading.
      { rootMargin: '-20% 0px -60% 0px' },
    );
    for (const id of ids) {
      const element = document.getElementById(sectionDomId(id));
      if (element) observer.observe(element);
    }
    return () => observer.disconnect();
  }, [shownKey]);

  const go = (id: SectionId): void => {
    setActive(id);
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document.getElementById(sectionDomId(id))?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  };

  // A section that has just left (Tests, with Online assessments switched off) hands the mark to the first.
  const current = shown.some((section) => section.id === active) ? active : shown[0]?.id;
  return (
    <nav className={styles.index} aria-label="Sections">
      {shown.map((section) => (
        <button key={section.id} type="button" className={styles.indexItem} aria-current={current === section.id ? 'true' : undefined} onClick={() => go(section.id)}>
          {section.label}
        </button>
      ))}
    </nav>
  );
}

interface CardProps {
  readonly form: UseFormReturn<OrganisationSettingsInput>;
  readonly canEdit: boolean;
}

function MoneyCard({ form, canEdit }: CardProps): ReactNode {
  const errors = form.formState.errors;
  const currencies = useWatch({ control: form.control, name: 'currencies' });
  return (
    <Card title="Money" meta="Amounts stay in the currency they were typed in">
      <fieldset className={styles.fieldset} disabled={!canEdit}>
        <Field label="Currencies in use" hint="Offered wherever an amount is typed" error={errors.currencies?.message}>
          {({ inputId, describedBy, isInvalid }) => (
            <Controller
              control={form.control}
              name="currencies"
              render={({ field }) => (
                <MultiSelect
                  id={inputId}
                  describedBy={describedBy}
                  isInvalid={isInvalid}
                  label="Currencies in use"
                  disabled={!canEdit}
                  options={CURRENCY_OPTIONS}
                  value={field.value}
                  onChange={(value) => field.onChange(value.filter(isCurrencyCode))}
                />
              )}
            />
          )}
        </Field>
        <Field label="Reporting currency" hint="What “Show converted” on reports converts into" error={errors.reportingCurrency?.message}>
          {({ inputId, describedBy, isInvalid }) => (
            <Controller
              control={form.control}
              name="reportingCurrency"
              render={({ field }) => (
                <Select
                  id={inputId}
                  describedBy={describedBy}
                  isInvalid={isInvalid}
                  label="Reporting currency"
                  disabled={!canEdit}
                  options={CURRENCY_OPTIONS.filter((option) => currencies.some((code) => code === option.value))}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          )}
        </Field>
        <ModuleGate module="money">
          <p className={styles.muted}>
            Rates for converting are kept under{' '}
            <Link className={styles.link} to="/admin/fx-rates">
              Exchange rates
            </Link>
            .
          </p>
        </ModuleGate>
      </fieldset>
    </Card>
  );
}

function DatesCard({ form, canEdit }: CardProps): ReactNode {
  return (
    <Card title="Dates and numbers">
      <fieldset className={styles.fieldset} disabled={!canEdit}>
        <div className={styles.pair}>
          <Field label="Default time zone" hint="New people, ventures and calendars start here">
            {({ inputId, describedBy }) => (
              <Controller
                control={form.control}
                name="defaultTimezone"
                render={({ field }) => <Select id={inputId} describedBy={describedBy} label="Default time zone" disabled={!canEdit} options={SUPPORTED_TIMEZONES} value={field.value} onChange={field.onChange} />}
              />
            )}
          </Field>
          <Field label="Fiscal year starts in" hint="Reports group by this year">
            {({ inputId, describedBy }) => (
              <Controller
                control={form.control}
                name="fiscalYearStartMonth"
                render={({ field }) => (
                  <Select id={inputId} describedBy={describedBy} label="Fiscal year starts in" disabled={!canEdit} options={MONTHS} value={String(field.value)} onChange={(value) => field.onChange(Number(value))} />
                )}
              />
            )}
          </Field>
        </div>
        <Controller
          control={form.control}
          name="dateFormat"
          render={({ field }) => (
            <fieldset className={styles.fieldset}>
              <legend className={styles.subhead}>Date format</legend>
              <RadioGroup isInline>
                {DATE_FORMATS.map((format) => (
                  <Radio key={format} name="dateFormat" value={format} checked={field.value === format} onChange={() => field.onChange(format)} disabled={!canEdit} label={DATE_FORMAT_LABELS[format]} />
                ))}
              </RadioGroup>
            </fieldset>
          )}
        />
        <Controller
          control={form.control}
          name="numberFormat"
          render={({ field }) => (
            <fieldset className={styles.fieldset}>
              <legend className={styles.subhead}>Large numbers</legend>
              <RadioGroup isInline>
                {NUMBER_FORMATS.map((format) => (
                  <Radio key={format} name="numberFormat" value={format} checked={field.value === format} onChange={() => field.onChange(format)} disabled={!canEdit} label={NUMBER_FORMAT_LABELS[format]} />
                ))}
              </RadioGroup>
            </fieldset>
          )}
        />
      </fieldset>
    </Card>
  );
}

function UploadsCard({ form, canEdit }: CardProps): ReactNode {
  const errors = form.formState.errors;
  return (
    <Card title="Uploads" meta={`Up to ${FILE_LIMIT_MAX_MB} MB each`}>
      <fieldset className={styles.fieldset} disabled={!canEdit}>
        <div className={styles.shortRow}>
          <Field label="Largest CV (MB)" error={errors.cvMaxMb?.message}>
            {({ inputId, describedBy, isInvalid }) => (
              <Input id={inputId} aria-describedby={describedBy} isInvalid={isInvalid} type="number" inputMode="numeric" min={1} max={FILE_LIMIT_MAX_MB} {...form.register('cvMaxMb', { valueAsNumber: true })} />
            )}
          </Field>
          <Field label="Largest other document (MB)" error={errors.documentMaxMb?.message}>
            {({ inputId, describedBy, isInvalid }) => (
              <Input id={inputId} aria-describedby={describedBy} isInvalid={isInvalid} type="number" inputMode="numeric" min={1} max={FILE_LIMIT_MAX_MB} {...form.register('documentMaxMb', { valueAsNumber: true })} />
            )}
          </Field>
        </div>
      </fieldset>
    </Card>
  );
}

function FinalSelectionCard({ form, canEdit }: CardProps): ReactNode {
  const errors = form.formState.errors;
  return (
    <Card title="Final selection" meta="On the candidate comparison">
      <fieldset className={styles.fieldset} disabled={!canEdit}>
        <Controller
          control={form.control}
          name="finalSelection.whoSelects"
          render={({ field }) => (
            <fieldset className={styles.fieldset}>
              <legend className={styles.subhead}>Who records the final selection</legend>
              <RadioGroup>
                {WHO_SELECTS.map((option) => (
                  <Radio key={option} name="whoSelects" value={option} checked={field.value === option} onChange={() => field.onChange(option)} disabled={!canEdit} label={WHO_SELECTS_LABELS[option]} />
                ))}
              </RadioGroup>
            </fieldset>
          )}
        />
        <Controller
          control={form.control}
          name="finalSelection.eliminate"
          render={({ field }) => (
            <fieldset className={styles.fieldset}>
              <legend className={styles.subhead}>What “Eliminate” does</legend>
              <RadioGroup>
                {ELIMINATE_MODES.map((option) => (
                  <Radio key={option} name="eliminate" value={option} checked={field.value === option} onChange={() => field.onChange(option)} disabled={!canEdit} label={ELIMINATE_MODE_LABELS[option]} />
                ))}
              </RadioGroup>
            </fieldset>
          )}
        />
        <div className={styles.shortRow}>
          <Field label="Keep someone on hold for (days)" hint={`Between ${HOLD_DAYS_MIN} and ${HOLD_DAYS_MAX}`} error={errors.finalSelection?.holdDays?.message}>
            {({ inputId, describedBy, isInvalid }) => (
              <Input id={inputId} aria-describedby={describedBy} isInvalid={isInvalid} type="number" inputMode="numeric" min={HOLD_DAYS_MIN} max={HOLD_DAYS_MAX} {...form.register('finalSelection.holdDays', { valueAsNumber: true })} />
            )}
          </Field>
        </div>
      </fieldset>
    </Card>
  );
}

function TestsCard({ form, canEdit }: CardProps): ReactNode {
  return (
    <Card title="Tests" meta="Shortlist shortcut">
      <fieldset className={styles.fieldset} disabled={!canEdit}>
        <Controller
          control={form.control}
          name="sendTestFromShortlist"
          render={({ field }) => <Switch checked={field.value} disabled={!canEdit} label="Send the test straight from Shortlist" onChange={field.onChange} />}
        />
        <p className={styles.muted}>
          When on, Shortlist has a “Send test” button. The people chosen skip the hiring manager’s Yes/No and Contact, move to Assessment and get the test — or, for a test
          taken outside the app, wait there for their result.
        </p>
      </fieldset>
    </Card>
  );
}
